import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for pull-to-refresh on mobile.
 *
 * Usage:
 *   const { pullToRefreshProps, PullToRefreshIndicator } = usePullToRefresh(onRefresh);
 *   return (
 *     <div {...pullToRefreshProps}>
 *       <PullToRefreshIndicator />
 *       ...content...
 *     </div>
 *   );
 */

const THRESHOLD = 80;    // px to pull before triggering refresh
const MAX_PULL = 120;    // max pull distance
const RESISTANCE = 0.45; // resistance factor for overscroll

export default function usePullToRefresh(onRefresh) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);

  const handleTouchStart = useCallback((e) => {
    // Only activate when scrolled to top
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    if (scrollTop <= 0 && !refreshing) {
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    }
  }, [refreshing]);

  const handleTouchMove = useCallback((e) => {
    if (!pulling.current || refreshing) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;

    if (diff > 0) {
      const distance = Math.min(diff * RESISTANCE, MAX_PULL);
      setPullDistance(distance);
    }
  }, [refreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;

    if (pullDistance >= THRESHOLD && onRefresh) {
      setRefreshing(true);
      setPullDistance(THRESHOLD * 0.6); // Hold at indicator position
      try {
        await onRefresh();
      } catch (err) {
        console.error('Pull-to-refresh error:', err);
      }
      setRefreshing(false);
    }
    setPullDistance(0);
  }, [pullDistance, onRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pulling.current = false;
    };
  }, []);

  const pullToRefreshProps = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };

  const progress = Math.min(pullDistance / THRESHOLD, 1);

  const PullToRefreshIndicator = () => {
    if (pullDistance <= 0 && !refreshing) return null;

    return (
      <div
        className="flex items-center justify-center overflow-hidden transition-all duration-200 ease-out"
        style={{
          height: refreshing ? 48 : pullDistance,
          opacity: refreshing ? 1 : progress,
        }}
      >
        <div
          className={`w-8 h-8 rounded-full border-[3px] border-[#9398DE]/30 border-t-[#9398DE] ${
            refreshing ? 'animate-spin' : ''
          }`}
          style={{
            transform: refreshing ? 'none' : `rotate(${progress * 360}deg)`,
            transition: refreshing ? 'none' : 'transform 0.1s ease-out',
          }}
        />
      </div>
    );
  };

  return { pullToRefreshProps, PullToRefreshIndicator, refreshing };
}
